/* eslint-disable no-param-reassign */
import React, {
  Context,
  useState,
  useEffect,
  useContext,
  useCallback,
} from 'react';
import { toast } from 'react-toastify';
import { useHistory } from 'react-router';
import { observer } from 'mobx-react-lite';
import { Tab, Tabs, CircularProgress } from '@material-ui/core';

import { instance } from '../../services/api';
import { FieldView } from '../../types/FieldView';
import { Inputs } from '../../types/Inputs';

import CurlPanel from '../../components/CurlPanel';
import { Paper, Typography, Button } from './styles';
import { getForm, fromJsonToFormContext } from './utils';
import { FormStoreContext, FormStore, resetFormStoreContext } from '../../store/FormStore';

import { GlobalsContext } from '../../store/Globals';

const FormGenerator: React.FC<{
  keyId: string;
  asset: any;
  readers: string[] | null;
  collection?: string;
}> = observer(({ asset, keyId, readers, collection }) => {
  const history = useHistory();
  const globals = useContext(GlobalsContext);
  const [url, setURL] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [method, setMethod] = useState<string>('');
  const [tabNumber, setTabNumber] = useState<number>(0);
  const [editAsset, setEditAsset] = useState<FieldView[]>([]);
  const [jsonObj, setJsonObj] = useState<Record<string, any>>({});
  const [initialInputs, setInitialInputs] = useState<Inputs[]>([]);
  const { dataTypeList } = globals;
  const context = useCallback(() => {
    let { inputs } = asset;

    if (asset.name.tag === 'documentHash') {
      inputs = asset.inputs.filter(
        (f: any) => f.tag !== 'filename' && f.tag !== 'hash',
      );
    }

    return FormStoreContext([
      ...inputs.map((f: any) => fromJsonToFormContext(f, dataTypeList)),
    ]);
  }, [asset, dataTypeList]);
  const Forms: FormStore = useContext(context() as Context<FormStore>);
  const { inputs } = Forms;

  const setFields = useCallback(
    async (
      schemas: FieldView[],
      data: { [index: string]: any; '@assetType'?: string },
    ): Promise<FieldView[]> => {
      Forms.clearInputs();
      const results = Promise.all(
        schemas.map(async (item) => {
          return {
            label: item.label,
            value: data[item.tag],
            disabled: item.isKey || item.readOnly,
            isKey: item.isKey,
            tag: item.tag,
            route: item.route,
            dataType: item.dataType,
          };
        }),
      );

      return results;
    },
    [Forms],
  );

  const getSchema = useCallback(
    async (res: { [x: string]: any; '@assetType': any }) => {
      try {
        const assetType =
          res['@assetType'].indexOf('[]') >= 0 ||
            res['@assetType'].indexOf('->') >= 0 ||
            res['@assetType'].indexOf('-%3E') >= 0
            ? res['@assetType']
              .replace('[]', '')
              .replace('->', '')
              .replace('-%3E', '')
            : res['@assetType'];

        const {
          data,
        }: {
          data: { props: { label: string; isKey: boolean; tag: string }[] };
        } = await instance.post('/api/query/getSchema/', {
          assetType,
        });

        return setFields(data.props as FieldView[], res);
      } catch {
        toast.error('API schema request fail');

        return [];
      }
    },
    [setFields],
  );

  const getItem = useCallback(
    async (assetTag: string, keyid: string) => {
      try {
        const { data } = await instance.post(`/api/query/readAsset`, {
          key: { '@assetType': assetTag, '@key': keyid },
          resolve: 'true',
        });

        return getSchema(data);
      } catch {
        try {
          const {
            data,
          } = await instance.post(
            `/api/query/readAsset?collections=${collection}`,
            { key: { '@assetType': assetTag, '@key': keyid }, resolve: 'true' },
          );
          return getSchema(data);
        } catch (error: any) {
          toast.error(error.message);
        }

        toast.error(`API ${assetTag} asset read request fail`);

        return [];
      }
    },
    [collection, getSchema],
  );

  const getItemValue = useCallback(
    async (assetType: string, keyid: string, privateAsset: boolean) => {
      try {
        if (!privateAsset) {
          return await instance.post(`/api/query/readAsset`, {
            key: { '@assetType': assetType, '@key': keyid },
            resolve: 'true',
          });
        }
        return await instance.post(`/api/query/readAsset?collections=${collection}`, {
          key: { '@assetType': assetType, '@key': keyid },
          resolve: 'true',
        });
      } catch {
        // Fallback: try with collection if non-private read failed
        try {
          return await instance.post(`/api/query/readAsset?collections=${collection}`, {
            key: { '@assetType': assetType, '@key': keyid },
            resolve: 'true',
          });
        } catch {
          toast.error(`API ${assetType} type search request fail`);
          return { data: { result: null } };
        }
      }
    },
    [collection],
  );

  // Copy an array with no references
  const copyArray = useCallback((aObject: any) => {
    if (!aObject) return aObject;

    const bObject = Array.isArray(aObject) ? [] : {};

    Object.entries(aObject).forEach((entrie) => {
      const key = entrie[0];
      const value = entrie[1];

      (bObject as any)[key] =
        typeof value === 'object' ? copyArray(value) : value;
    });

    return bObject;
  }, []);

  const setInitInputs = useCallback(async () => {
    const assetTag = keyId.split(':')[0];
    // get asset value
    if (assetTag) {
      const results = await getItem(assetTag, keyId);
      let res = await getItemValue(assetTag, keyId, false);
      if (res && res.data && res.data['@hash']) {
        res = await getItemValue(assetTag, keyId, true);
      }
      const { data } = res;
      const inputValue = data;

      const privatePromises = Object.values(data)
        .filter((prop: any) => prop != null && typeof prop === 'object' && !Array.isArray(prop) && prop['@hash'])
        .map((item: any) =>
          getItemValue(item['@assetType'], item['@key'], true).catch(
            () => null,
          ),
        );
      const privateAssets = await Promise.all(privatePromises);

      const assetPromises = Object.values(data)
        .filter((prop: any) => prop != null && typeof prop === 'object' && !Array.isArray(prop) && prop['@assetType'] && !prop['@hash'])
        .map((item: any) =>
          getItemValue(item['@assetType'], item['@key'], true).catch(
            () => null,
          ),
        );
      const assets = await Promise.all(assetPromises);

      inputs.forEach((input) => {
        const inputTag = inputValue
          ? Object.keys(inputValue).find((tag) => tag === input.tag)
          : null;

        if (inputTag) {
          const fieldVal = inputValue[inputTag];
          if (fieldVal != null && typeof fieldVal === 'object' && !Array.isArray(fieldVal) && (fieldVal as any)['@hash']) {
            const key = (fieldVal as any)['@key'];
            const privAsset = privateAssets.find(
              (item: any) => item && item.data && item.data['@key'] === key,
            );
            if (privAsset) input.value = privAsset.data;
            else input.value = null;
          } else {
            input.value = fieldVal;
          }

          if (input.value && typeof input.value === 'object' && !Array.isArray(input.value) && (input as any).value['@key']) {
            const assetFind = assets.find(
              (foundAsset: any) =>
                foundAsset &&
                foundAsset.data &&
                foundAsset.data['@key'] &&
                (foundAsset.data['@key'] as string) ===
                (input as any).value['@key'],
            );
            if (assetFind) {
              input.value = assetFind.data;
              Object.keys(assetFind.data).forEach((k: string) => {
                (input as any).value[k] = assetFind.data[k];
              });
            }
          }
        }
      });

      setEditAsset(results);
      setInitialInputs(copyArray(inputs));
    }
  }, [inputs, keyId, getItem, getItemValue, copyArray]);

  // Reset form store context on unmount to prevent stale data
  // when navigating between edit and create of the same asset type
  useEffect(() => {
    return () => {
      resetFormStoreContext();
    };
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);

      // Check if it is editing
      if (keyId && keyId !== '') {
        try {
          await setInitInputs();
        } catch (err: any) {
          console.error('Failed to load edit data:', err);
          toast.error('Failed to load asset data for editing');
        }

        setLoading(false);
      } else {
        // Zero all inputs for creating
        inputs.forEach((input) => {
          input.value = null;
          input.selectorValue = null;
        });

        setLoading(false);
      }
    })();
  }, [keyId, inputs, readers, getItem, getItemValue, setInitInputs]);

  // Verify if any input had any modification
  const verifyInputDiff = () => {
    inputs.forEach((input: any) => {
      if (input.dataType === 'boolean') {
        if (input.value === null) return;
      } else if (!input.value) return;

      const checkInput: Inputs | undefined = initialInputs.find(
        (initialInput) => initialInput.tag === input.tag,
      );

      if (!checkInput) return;

      // Check if the input is an asset array
      if (
        checkInput &&
        Array.isArray(input.value) &&
        Array.isArray(checkInput.value) &&
        input.value.length === checkInput.value.length
      ) {
        const diffArray = input.value.some((item: any) => {
          const value = Array.isArray(checkInput.value) ? checkInput.value : [];

          const index = value.findIndex(
            (el: any) =>
              (typeof el !== 'undefined' &&
                typeof item !== 'undefined' &&
                Object.prototype.hasOwnProperty.call(el, '@key') &&
                Object.prototype.hasOwnProperty.call(item, '@key') &&
                el['@key'] === item['@key']) ||
              el === item,
          );

          return index < 0;
        });

        if (!diffArray) input.value = null;

        // check if the input (not array) has changed
      } else if (
        (checkInput &&
          checkInput.value !== null &&
          typeof checkInput.value === 'object' &&
          Object.prototype.hasOwnProperty.call(input.value, '@key') &&
          Object.prototype.hasOwnProperty.call(checkInput.value, '@key') &&
          // I don't know what to do about this...
          (checkInput.value as any)['@key'] === (input.value as any)['@key']) ||
        (typeof checkInput.value !== 'object' &&
          checkInput.value === input.value)
      ) {
        input.value = null;
      }
    });
  };

  // Key Inputs do not change
  const maintainKeyValues = () => {
    inputs.forEach((input) => {
      if (editAsset.find((e) => e.tag === input.tag && e.isKey)) {
        const keyValue = initialInputs.find((init: any) => init.tag === input.tag);
        if (keyValue) {
          input.value = keyValue.value;
        }
      }
    });
  };

  const submitCreateHandle = async (execute: boolean, inputsData: object) => {
    if (readers && readers.length > 0) {
      const collections = readers
        ? `?@collections=${btoa(`["${collection}"]`)}`
        : '';

      if (execute) {
        await instance.post(`/api/invoke/createAsset${collections}`, {
          '~asset': [
            {
              '@assetType': asset.name.tag,
              ...inputsData,
            },
          ],
        });
      } else {
        setMethod('POST');
        setURL(`/api/invoke/createAsset${collections}`);
        setJsonObj({
          '~asset': [{ '@assetType': asset.name.tag, ...inputsData }],
        });
      }
    } else if (execute) {
      await instance.post('/api/invoke/createAsset', {
        asset: [
          {
            '@assetType': asset.name.tag,
            ...inputsData,
          },
        ],
      });
    } else {
      setMethod('POST');
      setURL('/api/invoke/createAsset');
      setJsonObj({ asset: [{ '@assetType': asset.name.tag, ...inputsData }] });
    }
  };

  const submitEditHandle = async (execute: boolean, inputsData: object) => {
    if (readers && readers.length > 0) {
      if (execute) {
        await instance.put('/api/invoke/updateAsset', {
          '~update': {
            '@assetType': asset.name.tag,
            ...inputsData,
          },
        });
      } else {
        setMethod('PUT');
        setURL('/api/invoke/updateAsset');
        setJsonObj({
          '~update': { '@assetType': asset.name.tag, ...inputsData },
        });
      }
    } else if (execute) {
      await instance.put('/api/invoke/updateAsset', {
        update: {
          '@assetType': asset.name.tag,
          ...inputsData,
        },
      });
    } else {
      var jsonObj: any = { update: { "@assetType": asset.name.tag, ...inputsData } };
      setMethod('PUT');
      setURL('/api/invoke/updateAsset');
      setJsonObj(jsonObj);
    }

    if (execute) history.push(`/${asset.name.tag}/list`);
  };

  // Submit a create or edit transaction
  const submit = async (execute: boolean) => {
    setLoading(true);

    verifyInputDiff();
    maintainKeyValues();

    const inputsData = Forms.getData();

    Object.keys(inputsData).forEach(
      (key) =>
        (inputsData[key] == null || inputsData[key] === '') &&
        delete inputsData[key],
    );

    if (execute) Forms.toggleInputsDisabled();

    try {
      delete inputsData['@key'];

      const inp = inputsData;

      if (!keyId || keyId === '') {
        await submitCreateHandle(execute, inputsData);
      } else {
        await submitEditHandle(execute, inp);
      }

      if (execute) {
        const message =
          !keyId || keyId === ''
            ? `${asset.name.label} created successfully`
            : `${asset.name.label} edited successfully`;

        toast.success(message);
      }

      setLoading(false);
    } catch (err: any) {
      setLoading(false);

      const message =
        !keyId || keyId === ''
          ? `Error while trying to create ${asset.name.label}: ${err.response ? err.response.data?.error : err
          }`
          : `Error while trying to edit ${asset.name.label}: ${err.response ? err.response.data?.error : err
          }`;
      toast.error(message);
    }

    if (execute) {
      Forms.toggleInputsDisabled();
      Forms.clearInputs();
    }
  };

  return (
    <div style={{ marginBottom: '30px' }}>
      <CurlPanel method={method} url={url} obj={jsonObj} update={submit} />
      <Paper style={{ padding: '30px', marginTop: '10px' }}>
        {readers && readers.length > 0 ? (
          <i className="bx bx-lock-alt" style={{ color: '#000099', marginRight: '2%' }} />
        ) : null}

        <Typography>{asset.name.label}</Typography>

        {inputs.length > 8 ? (
          <Tabs
            value={tabNumber}
            indicatorColor="primary"
            textColor="primary"
            variant="scrollable"
            scrollButtons="auto"
            TabIndicatorProps={{ style: { backgroundColor: 'blue' } }}
            onChange={(_, tab) => {
              setTabNumber(tab);
            }}
            aria-label="disabled tabs example"
          >
            {inputs.map((_, index) => {
              return index % 8 === 0 ? (
                <Tab key={`tab${index}`} label={index / 8} />
              ) : null;
            })}
          </Tabs>
        ) : null}

        {inputs
          .filter(
            (_, index) => index >= tabNumber * 8 && index < (tabNumber + 1) * 8,
          )
          .map((form: any) => getForm(form, keyId))}

        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <CircularProgress />
          </div>
        ) : (
          <Button
            style={{ margin: '0 auto', width: '640px' }}
            onClick={() => submit(true)}
          >
            Submit
          </Button>
        )}
      </Paper>
    </div>
  );
});

export default FormGenerator;
